// src/components/onboarding/address-autocomplete.tsx
import { useEffect, useRef } from 'react';
import { useGoogleMapsScript } from '@/src/hooks/useGoogleMapsScript';
import { useAppColors } from '@/src/lib/theme';

export interface AddressResult {
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  latitude: number;
  longitude: number;
}

interface AddressAutocompleteProps {
  defaultValue?: string;
  onSelect: (result: AddressResult) => void;
}

// deno-lint-ignore no-explicit-any
type GoogleGlobal = any;

export default function AddressAutocomplete({ defaultValue, onSelect }: AddressAutocompleteProps) {
  const c = useAppColors();
  const loaded = useGoogleMapsScript();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<GoogleGlobal>(null);

  useEffect(() => {
    if (!loaded || !inputRef.current || autocompleteRef.current) return;
    const google = (window as unknown as { google: GoogleGlobal }).google;
    const ac = new google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'au' },
      fields: ['address_components', 'geometry', 'formatted_address'],
      types: ['address'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.address_components || !place.geometry?.location) return;

      let streetNumber = '';
      let route = '';
      let suburb = '';
      let state = '';
      let postcode = '';
      for (const comp of place.address_components) {
        const types = comp.types as string[];
        if (types.includes('street_number')) streetNumber = comp.long_name;
        if (types.includes('route')) route = comp.long_name;
        if (types.includes('locality')) suburb = comp.long_name;
        if (types.includes('administrative_area_level_1')) state = comp.short_name;
        if (types.includes('postal_code')) postcode = comp.long_name;
      }

      onSelect({
        address: [streetNumber, route].filter(Boolean).join(' '),
        suburb,
        state,
        postcode,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
      });
    });
    autocompleteRef.current = ac;
  }, [loaded, onSelect]);

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: c.textFaint }}>Address</label>
      <input
        ref={inputRef}
        defaultValue={defaultValue}
        placeholder={loaded ? 'Start typing your address…' : 'Loading address search…'}
        disabled={!loaded}
        className="w-full h-11 rounded-lg px-3 text-sm outline-none disabled:opacity-50"
        style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }}
      />
    </div>
  );
}